// Case Create/Edit Form Page
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { 
  ArrowLeft, 
  Save, 
  Loader2,
  AlertTriangle,
  User,
  Building2,
  DollarSign,
  FileCheck,
  Heart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';

export function CaseFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const yearContext = searchParams.get('year');
  const { user, hasPermission } = useAuth();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [constants, setConstants] = useState(null);
  const [years, setYears] = useState([]);
  const [users, setUsers] = useState([]);
  const [intakeTypes, setIntakeTypes] = useState(null); // active intake types for the selected coverage year
  
  const [formData, setFormData] = useState({
    coverage_year_id: '',
    intake_type: 'New Enrollment',
    premium_support_status: 'Pending',
    sep_life_event_date: '',
    sep_life_event_notes: '',
    athena_id: '',
    case_manager_id: '',
    office: 'Wilton Manors',
    preferred_language: 'English',
    program_type: 'PrEP',
    information_collected_date: new Date().toISOString().split('T')[0],
    
    // Eligibility
    ssn: '',
    immigration_status: '',
    currently_smokes: false,
    marital_status: '',
    has_dependents: false,
    dependents_count: null,
    
    // Financial
    occupation: '',
    has_income: true,
    income_period: 'monthly',
    hourly_income: '',
    weekly_income: '',
    monthly_income: '',
    annual_income: '',
    hours_per_week: '',
    zero_income: false,
    income_hard_to_estimate: false,
    estimated_annual_income: '',
    will_file_taxes: true,
    files_taxes_separately: false,
    
    communication_preference: 'Email / Paperless',
    
    // Consent
    consent_status: 'Consent Pending',
    consent_date: '',
    consent_collected_by: '',
    consent_method: '',
    consent_notes: '',
    
    // Spouse
    has_spouse: false,
    spouse: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      occupation: '',
      has_income: true,
      income_period: 'monthly',
      monthly_income: '',
      ssn: '',
      spouse_applying: false,
      file_jointly: true
    },
    
    notes: '',
    urgent: false
  });

  useEffect(() => {
    loadInitialData();
  }, [id]);

  // Intake types are configured per coverage year — reload the active list when the year changes.
  useEffect(() => {
    if (!formData.coverage_year_id) { setIntakeTypes(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await portalApi.getActiveIntakeTypes(formData.coverage_year_id);
        if (cancelled) return;
        const list = res?.intake_types || [];
        setIntakeTypes(list);
        // For NEW cases, if the current selection isn't active for this year, pick the first available.
        if (!isEdit && list.length && !list.includes(formData.intake_type)) {
          setFormData(prev => ({ ...prev, intake_type: list[0] }));
        }
      } catch (e) {
        if (!cancelled) setIntakeTypes(null);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.coverage_year_id]);

  const loadInitialData = async () => {
    try {
      // Any user who can create cases should be able to pick/see the case manager selector.
      const canAssign = hasPermission('cases.create');
      const [constantsData, yearsData, usersData] = await Promise.all([
        portalApi.getConstants(),
        portalApi.getCoverageYears(),
        canAssign ? portalApi.getAssignableUsers().catch(() => []) : Promise.resolve([])
      ]);
      
      setConstants(constantsData);
      // For NEW cases, only ACTIVE (non-archived) coverage years are selectable.
      // When editing, keep the case's own year available even if now inactive.
      const selectableYears = id
        ? yearsData.filter(y => y.accessible !== false && !y.is_archived)
        : yearsData.filter(y => y.accessible !== false && !y.is_archived && y.is_active !== false);
      setYears(selectableYears);
      setUsers(usersData);
      
      // Set default year: dashboard year context (URL ?year=) wins, else default/first active
      if (!id) {
        const contextYear = yearContext && selectableYears.find(y => y.id === yearContext);
        const defaultYear = contextYear || selectableYears.find(y => y.is_default) || selectableYears[0];
        setFormData(prev => ({
          ...prev,
          ...(defaultYear ? { coverage_year_id: defaultYear.id } : {}),
          // Case-handling roles default as the case manager (editable below).
          ...((user?.role === 'case_manager' || user?.role === 'program_manager') && !prev.case_manager_id ? { case_manager_id: user.id } : {}),
        }));
      }
      
      // Load existing case if editing
      if (id) {
        const caseData = await portalApi.getCase(id, hasPermission('cases.reveal_ssn'));
        setFormData(prev => ({
          ...prev,
          ...caseData,
          has_spouse: Boolean(caseData.spouse?.first_name),
          spouse: caseData.spouse || prev.spouse
        }));
      }
    } catch (err) {
      toast.error('Error loading data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpouseChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      spouse: { ...prev.spouse, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.athena_id || !formData.athena_id.match(/^\d+$/)) {
      toast.error('Athena ID must be a valid number');
      return;
    }
    
    if (!formData.coverage_year_id) {
      toast.error('Please select a coverage year');
      return;
    }

    const isMarried = formData.marital_status === 'Married';
    if (isMarried && formData.spouse.spouse_applying && !formData.spouse.ssn) {
      toast.error('Unable to create case. Spouse SSN is required when the spouse is also applying.');
      return;
    }
    // Spouse income: only required when the spouse HAS income.
    const spousePeriod = formData.spouse.income_period || 'monthly';
    const spouseHasIncome = isMarried && formData.spouse.has_income !== false;
    if (spouseHasIncome) {
      const amt = formData.spouse[`${spousePeriod}_income`];
      if (amt === '' || amt === null || amt === undefined) {
        toast.error('Unable to create case. Please enter the spouse income amount, or uncheck "Spouse has income".');
        return;
      }
    }

    const num = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));

    setSaving(true);
    try {
      let spousePayload = null;
      if (isMarried) {
        if (formData.spouse.has_income === false) {
          // Spouse has no income — record $0, clear all income amounts.
          spousePayload = {
            ...formData.spouse,
            has_income: false,
            hourly_income: null,
            weekly_income: null,
            monthly_income: 0,
            annual_income: null,
            hours_per_week: null,
          };
        } else {
          spousePayload = {
            ...formData.spouse,
            has_income: true,
            income_period: spousePeriod,
            hourly_income: num(formData.spouse.hourly_income),
            weekly_income: num(formData.spouse.weekly_income),
            monthly_income: num(formData.spouse.monthly_income),
            annual_income: num(formData.spouse.annual_income),
            hours_per_week: num(formData.spouse.hours_per_week),
          };
        }
      }

      const submitData = {
        ...formData,
        spouse: spousePayload,
        hourly_income: num(formData.hourly_income),
        weekly_income: num(formData.weekly_income),
        monthly_income: num(formData.monthly_income),
        annual_income: num(formData.annual_income),
        hours_per_week: num(formData.hours_per_week),
        estimated_annual_income: num(formData.estimated_annual_income),
      };
      
      if (isEdit) {
        await portalApi.updateCase(id, submitData);
        toast.success('Case updated successfully');
      } else {
        const result = await portalApi.createCase(submitData);
        toast.success('Case created successfully');
        navigate(`/portal/cases/${result.id}`);
        return;
      }
      
      navigate(`/portal/cases/${id}`);
    } catch (err) {
      toast.error(err.message || 'Unable to create the case due to a system error. Please try again. If the problem continues, contact an administrator.');
    } finally {
      setSaving(false);
    }
  };

  const undocumentedSelected = formData.immigration_status === 'Undocumented';
  const noIncome = !formData.has_income || formData.zero_income;
  const expectedIsZero = noIncome && formData.estimated_annual_income !== '' && parseFloat(formData.estimated_annual_income) === 0;
  const showNotEligibleAlert = undocumentedSelected || expectedIsZero;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="case-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isEdit ? 'Edit Case' : 'New Case'}
          </h1>
          {isEdit && (
            <p className="text-slate-500">Athena ID: {formData.athena_id}</p>
          )}
        </div>
      </div>

      {showNotEligibleAlert && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="not-eligible-alert">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Based on the information provided, this client is not eligible for premium subsidies. However, we will examine whether we can assist with 100% of their insurance premium.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <User className="h-5 w-5 text-sky-500" />
            <h2 className="text-lg font-semibold text-slate-900">Basic Information</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Coverage Year <span className="text-red-500">*</span></Label>
              <Select value={formData.coverage_year_id} onValueChange={(v) => handleChange('coverage_year_id', v)}>
                <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                <SelectContent>
                  {years.map(y => (
                    <SelectItem key={y.id} value={y.id}>{y.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Intake Type</Label>
              <Select value={formData.intake_type} onValueChange={(v) => handleChange('intake_type', v)}>
                <SelectTrigger data-testid="intake-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const base = intakeTypes || constants?.intake_types || [];
                    // Always keep the currently-selected type visible (e.g. editing a case whose type is now off for this year).
                    const opts = formData.intake_type && !base.includes(formData.intake_type)
                      ? [formData.intake_type, ...base] : base;
                    return opts.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ));
                  })()}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Premium Support Status</Label>
              <Select value={formData.premium_support_status} onValueChange={(v) => handleChange('premium_support_status', v)}>
                <SelectTrigger data-testid="premium-support-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {constants?.premium_support_statuses?.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Athena ID <span className="text-red-500">*</span></Label>
              <Input
                value={formData.athena_id}
                onChange={(e) => handleChange('athena_id', e.target.value)}
                placeholder="12345"
                data-testid="input-athena-id"
              />
            </div>

            <div className="space-y-2">
              <Label>Office</Label>
              <Select value={formData.office} onValueChange={(v) => handleChange('office', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {constants?.offices?.map(o => (
                    <SelectItem key={o} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Program Type</Label>
              <Select value={formData.program_type} onValueChange={(v) => handleChange('program_type', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {constants?.program_types?.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Preferred Language</Label>
              <Select value={formData.preferred_language} onValueChange={(v) => handleChange('preferred_language', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {constants?.languages?.map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {users.length > 0 && (
              <div className="space-y-2">
                <Label>Case Manager</Label>
                <Select value={formData.case_manager_id || 'unassigned'} onValueChange={(v) => handleChange('case_manager_id', v === 'unassigned' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Information Collected Date</Label>
              <Input
                type="date"
                value={formData.information_collected_date}
                onChange={(e) => handleChange('information_collected_date', e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2 pt-8">
              <Checkbox
                id="urgent"
                checked={formData.urgent}
                onCheckedChange={(v) => handleChange('urgent', v)}
              />
              <Label htmlFor="urgent" className="flex items-center gap-2 text-red-600 cursor-pointer">
                <AlertTriangle className="h-4 w-4" />
                Mark as Urgent
              </Label>
            </div>
          </div>
        </div>

        {/* SEP Life Event - only for Special Enrollment Period */}
        {formData.intake_type === 'Special Enrollment Period' && (
          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="sep-details-card">
            <div className="flex items-center gap-2 mb-6">
              <AlertTriangle className="h-5 w-5 text-sky-500" />
              <h2 className="text-lg font-semibold text-slate-900">Special Enrollment Period (SEP) Details</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Qualifying Life Event Date</Label>
                <Input
                  type="date"
                  value={formData.sep_life_event_date || ''}
                  onChange={(e) => handleChange('sep_life_event_date', e.target.value)}
                  data-testid="input-sep-date"
                />
              </div>
              <div className="space-y-2 md:col-span-3">
                <Label>Life Event Notes</Label>
                <Textarea
                  rows={3}
                  value={formData.sep_life_event_notes || ''}
                  onChange={(e) => handleChange('sep_life_event_notes', e.target.value)}
                  placeholder="Describe the qualifying life event: when they moved, when previous coverage ended, loss of coverage date, marriage, birth, etc."
                  data-testid="input-sep-notes"
                />
              </div>
            </div>
          </div>
        )}

        {/* Eligibility Information */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <FileCheck className="h-5 w-5 text-sky-500" />
            <h2 className="text-lg font-semibold text-slate-900">Eligibility Information</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>SSN</Label>
              <Input
                value={formData.ssn}
                onChange={(e) => handleChange('ssn', e.target.value)}
                placeholder="XXX-XX-XXXX"
                data-testid="input-ssn"
              />
            </div>

            <div className="space-y-2">
              <Label>Immigration Status</Label>
              <Select value={formData.immigration_status || 'none'} onValueChange={(v) => handleChange('immigration_status', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {constants?.immigration_statuses?.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Marital Status</Label>
              <Select value={formData.marital_status || 'none'} onValueChange={(v) => handleChange('marital_status', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {constants?.marital_statuses?.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 pt-8">
              <Checkbox
                id="currently_smokes"
                checked={formData.currently_smokes}
                onCheckedChange={(v) => handleChange('currently_smokes', v)}
              />
              <Label htmlFor="currently_smokes" className="cursor-pointer">Currently Smokes</Label>
            </div>

            <div className="flex items-center gap-2 pt-8">
              <Checkbox
                id="has_dependents"
                checked={formData.has_dependents}
                onCheckedChange={(v) => handleChange('has_dependents', v)}
              />
              <Label htmlFor="has_dependents" className="cursor-pointer">Has Dependents</Label>
            </div>

            {formData.has_dependents && (
              <div className="space-y-2">
                <Label htmlFor="dependents_count"># of Dependents (for household size / FPL)</Label>
                <Input
                  id="dependents_count"
                  type="number"
                  min="0"
                  value={formData.dependents_count ?? ''}
                  onChange={(e) => handleChange('dependents_count', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  placeholder="e.g. 2"
                  data-testid="case-dependents-count"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Communication Preference</Label>
              <Select value={formData.communication_preference || 'none'} onValueChange={(v) => handleChange('communication_preference', v === 'none' ? '' : v)}>
                <SelectTrigger><SelectValue placeholder="Select preference" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {constants?.communication_preferences?.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Financial Information */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <DollarSign className="h-5 w-5 text-sky-500" />
            <h2 className="text-lg font-semibold text-slate-900">Financial Information</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Occupation</Label>
              <Input
                value={formData.occupation}
                onChange={(e) => handleChange('occupation', e.target.value)}
                placeholder="Enter occupation"
              />
            </div>

            <div className="flex items-center gap-2 pt-8">
              <Checkbox
                id="has_income"
                checked={formData.has_income}
                onCheckedChange={(v) => {
                  handleChange('has_income', v);
                  if (v) handleChange('zero_income', false);
                }}
              />
              <Label htmlFor="has_income" className="cursor-pointer">Has Income</Label>
            </div>

            {!formData.has_income && (
              <div className="flex items-center gap-2 pt-8">
                <Checkbox
                  id="zero_income"
                  checked={formData.zero_income}
                  onCheckedChange={(v) => handleChange('zero_income', v)}
                />
                <Label htmlFor="zero_income" className="cursor-pointer">Zero Income</Label>
              </div>
            )}

            {formData.has_income && !formData.zero_income && (
              <>
                <div className="space-y-2">
                  <Label>Income Period</Label>
                  <Select value={formData.income_period || 'monthly'} onValueChange={(v) => handleChange('income_period', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {constants?.income_periods?.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.income_period === 'hourly' && (
                  <>
                    <div className="space-y-2">
                      <Label>Hourly Income ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.hourly_income}
                        onChange={(e) => handleChange('hourly_income', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Hours per Week</Label>
                      <Input
                        type="number"
                        value={formData.hours_per_week}
                        onChange={(e) => handleChange('hours_per_week', e.target.value)}
                        placeholder="40"
                      />
                    </div>
                  </>
                )}

                {formData.income_period === 'weekly' && (
                  <div className="space-y-2">
                    <Label>Weekly Income ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.weekly_income}
                      onChange={(e) => handleChange('weekly_income', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                )}

                {formData.income_period === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Monthly Income ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.monthly_income}
                      onChange={(e) => handleChange('monthly_income', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                )}

                {formData.income_period === 'annual' && (
                  <div className="space-y-2">
                    <Label>Annual Income ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.annual_income}
                      onChange={(e) => handleChange('annual_income', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                )}
              </>
            )}

            {(!formData.has_income || formData.zero_income) && (
              <div className="space-y-2 md:col-span-2">
                <Label>Expected income for the current year (to report for next year) ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.estimated_annual_income}
                  onChange={(e) => handleChange('estimated_annual_income', e.target.value)}
                  placeholder="0.00"
                  data-testid="input-expected-income"
                />
              </div>
            )}

            <div className="flex items-center gap-2 pt-8">
              <Checkbox
                id="will_file_taxes"
                checked={formData.will_file_taxes}
                onCheckedChange={(v) => handleChange('will_file_taxes', v)}
              />
              <Label htmlFor="will_file_taxes" className="cursor-pointer">Will File Taxes</Label>
            </div>
          </div>
        </div>

        {/* Spouse Information - only when married */}
        {formData.marital_status === 'Married' && (
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-sky-500" />
              <h2 className="text-lg font-semibold text-slate-900">Spouse Information</h2>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="spouse_applying"
                checked={formData.spouse.spouse_applying}
                onCheckedChange={(v) => handleSpouseChange('spouse_applying', v)}
                data-testid="spouse-applying-checkbox"
              />
              <Label htmlFor="spouse_applying" className="cursor-pointer">Spouse is also applying</Label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>First Name</Label>
              <Input
                value={formData.spouse.first_name}
                onChange={(e) => handleSpouseChange('first_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name</Label>
              <Input
                value={formData.spouse.last_name}
                onChange={(e) => handleSpouseChange('last_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={formData.spouse.date_of_birth}
                onChange={(e) => handleSpouseChange('date_of_birth', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Occupation</Label>
              <Input
                value={formData.spouse.occupation}
                onChange={(e) => handleSpouseChange('occupation', e.target.value)}
              />
            </div>
            <div className="space-y-2 flex flex-col justify-end">
              <div className="flex items-center gap-2 h-10">
                <Checkbox
                  id="spouse_has_income"
                  checked={formData.spouse.has_income !== false}
                  onCheckedChange={(v) => handleSpouseChange('has_income', Boolean(v))}
                  data-testid="spouse-has-income-checkbox"
                />
                <Label htmlFor="spouse_has_income" className="cursor-pointer">Spouse has income</Label>
              </div>
            </div>
            {formData.spouse.has_income !== false ? (
              <>
                <div className="space-y-2">
                  <Label>Income Period</Label>
                  <Select value={formData.spouse.income_period || 'monthly'} onValueChange={(v) => handleSpouseChange('income_period', v)}>
                    <SelectTrigger data-testid="spouse-income-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(constants?.income_periods || ['hourly','weekly','monthly','annual']).map(p => (
                        <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>
                    {(formData.spouse.income_period === 'hourly' && 'Hourly Income ($)')
                      || (formData.spouse.income_period === 'weekly' && 'Weekly Income ($)')
                      || (formData.spouse.income_period === 'annual' && 'Annual Income ($)')
                      || 'Monthly Income ($)'} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.spouse[`${formData.spouse.income_period || 'monthly'}_income`] ?? ''}
                    onChange={(e) => handleSpouseChange(`${formData.spouse.income_period || 'monthly'}_income`, e.target.value)}
                    data-testid="spouse-income-amount"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2 flex items-end">
                <p className="text-sm text-slate-500" data-testid="spouse-no-income-note">No spouse income will be recorded ($0).</p>
              </div>
            )}
            {formData.spouse.spouse_applying && (
              <div className="space-y-2">
                <Label>Spouse SSN <span className="text-red-500">*</span></Label>
                <Input
                  value={formData.spouse.ssn}
                  onChange={(e) => handleSpouseChange('ssn', e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  data-testid="input-spouse-ssn"
                />
              </div>
            )}
            <div className="flex items-center gap-2 pt-8">
              <Checkbox
                id="file_jointly"
                checked={formData.spouse.file_jointly}
                onCheckedChange={(v) => handleSpouseChange('file_jointly', v)}
              />
              <Label htmlFor="file_jointly" className="cursor-pointer">File Jointly</Label>
            </div>
          </div>
        </div>
        )}

        {/* Notes */}
        <div className="bg-white rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-6">
            <Building2 className="h-5 w-5 text-sky-500" />
            <h2 className="text-lg font-semibold text-slate-900">Additional Notes</h2>
          </div>
          <Textarea
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Any additional notes about this case..."
            rows={4}
          />
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} data-testid="save-case-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {isEdit ? 'Update Case' : 'Create Case'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default CaseFormPage;
